import React, { useState, useEffect, useRef, useCallback } from "react";
import { Button, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { trackEvent } from "@/utils/analytics";

import {
  resendVerificationCode,
  sendVerificationCode,
  updateUserDetails,
  validateVerificationCode,
} from "@/api/LoginSignup";
import "./otpVerification.css";
import UserProfileForm from "@/components/form/UserProfileForm";
import { validateForm } from "@/utils/formValidator";
import { isPhoneNoVerified } from "@/api/bid";
import {
  getEventCategory,
  trackApiCall,
  trackButtonClick,
  trackPageMount,
} from "@/utils/analytics";
import LoadingText from "@/components/loader/LoadingText";
import LoadedText from "@/components/loader/LoadedText";
import { checkLoginStatus, getUserProfile } from "@/api/myProfile";
import { setProfileInfo, userData } from "@/redux/user/userSlice";

const OTPVerification = ({
  phoneNumber,
  isModal,
  setIsConfirmProfileActive,
  isFromGlobalOtpModal,
  onClose,
  isFromLoginSignupPage,
  otpErrorMessage,
  redirectScreen = "HOME",
  redirectionRequried = false,
}) => {
  const otpInputRefs = useRef([]);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userProfData = useSelector(
    (state) => state?.user?.profileInfo?.userDetails
  );
  const userData = useSelector((state) => state?.user);

  const userDataDetails = useSelector((state) => state?.user?.user);
  const [otp, setOtp] = useState(["", "", "", ""]);
  const [showProfileConfirmation, setShowProfileConfirmation] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isResendLoading, setIsResendLoading] = useState(false);
  const [isOtpVerificationGoing, setIsOtpVerificationGoing] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [resendOtpState, setResendOtp] = useState(false);
  const [isUpdateProfileModalActive, setIsUpdateProfileModalActive] =
    useState(false);

  const [userDetails, setUserDetails] = useState({
    firstName: userProfData?.firstName || userDataDetails?.userFirstName,
    lastName: userProfData?.lastName || userDataDetails?.userLastName,
    phoneNumber: userProfData?.phoneNumber || userDataDetails?.primaryPhone,
    email: userProfData?.emailId || userDataDetails?.userEmailId,
  });

  const [isUserDetailsUpdating, setIsUserDetailsUpdating] = useState(false);
  const [changePhoneErrorMsg, setChangePhoneErrorMsg] = useState("");
  const [formError, setFormError] = useState({});
  const [shouldSendOTP, setShouldSendOTP] = useState(true);

  // helper function to get user ID from either userProfData or userDataDetails
  const getUserId = () =>
    userProfData?.realbidUserId || userDataDetails?.realbidUserId;

  const maskPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) {
      phoneNumber = userProfData?.phoneNumber;
    }

    if (!phoneNumber) return "";
    return `(***)***-${phoneNumber.slice(-4)}`;
  };

  const sendOTP = async (options = {}) => {
    const { mannualClick = false } = options;

    try {
      setErrorMessage("");
      setResendOtp(false);
      setIsResendLoading(true);

      const payload = {
        phoneNumber: isFromGlobalOtpModal
          ? userDetails.phoneNumber
          : phoneNumber,
        verificationType: "sms",
      };

      if (payload.phoneNumber) {
        const response = await sendVerificationCode(payload);

        if (response?.status === "SUCCESS") {
          setShouldSendOTP(false);
          setResendOtp(true);
          setErrorMessage("");
        } else if (response.status === "FAILURE") {
          // Handle other error codes
          const apiErrorMessage =
            response?.errorCode || "Failed to send verification code.";

          if (
            mannualClick &&
            apiErrorMessage === "PHONE_NUMBER_ALREADY_VERIFIED"
          ) {
            setErrorMessage("This phone number is already verified.");
            setTimeout(() => {
              setErrorMessage("");
              if (isFromGlobalOtpModal) {
                setIsUpdateProfileModalActive(false);
                // Navigate based on redirectScreen before closing modal
                onClose && onClose();
              } else {
                navigate("/");
              }
            }, 3000);
          } else {
            // Map error codes to user-friendly messages
            const errorMessages = {
              VERIFICATION_CODE_LIMIT_EXEEDED:
                "You've exceeded number of attempts, please try again after 5 minutes.",
              TECHNICAL_ERROR:
                "There is a technical error. Please try again later.",
              PHONE_NUMBER_ALREADY_IN_USE:
                "This phone number is already in use. Please use a different number.",
              PHONE_NUMBER_ALREADY_VERIFIED:
                "This phone number is already verified for another user. Please use a different number.",
            };

            setErrorMessage(
              apiErrorMessage.startsWith("Invalid")
                ? "We are facing technical difficulties, please try again later."
                : errorMessages[apiErrorMessage] || apiErrorMessage
            );
          }
        }
      }
    } catch (error) {
      console.error("Error sending verification code:", error);
      setErrorMessage(
        "We are facing technical difficulties, please try again later."
      );
    } finally {
      setIsResendLoading(false);
    }
  };

  const handleUserDetailsChange = (e) => {
    const { name, value } = e.target;

    if (name === "phoneNumber") {
      const phoneNumber = value.replace(/\D/g, "");
      if (phoneNumber.length <= 10) {
        setUserDetails((prev) => ({
          ...prev,
          [name]: phoneNumber,
        }));
      }
      return;
    }

    // Update user details
    setUserDetails((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Validate field immediately
    const validationErrors = validateForm({ ...userDetails, [name]: value }, [
      name,
    ]);

    if (validationErrors.length > 0) {
      setFormError((prevErrors) => ({
        ...prevErrors,
        [name]: validationErrors[name],
      }));
    } else {
      setFormError((prevErrors) => ({
        ...prevErrors,
        [name]: "",
      }));
    }
  };

  const handleUpdateUserDetails = async (e) => {
    e.preventDefault();
    setChangePhoneErrorMsg("");

    // Validate form fields
    const validationErrors = validateForm(userDetails, [
      "firstName",
      "lastName",
      "email",
      "phoneNumber",
    ]);

    if (Object.keys(validationErrors).length > 0) {
      setFormError(validationErrors);
      return;
    }

    // Prepare payload
    const payload = {
      firstName: userDetails.firstName?.trim(),
      lastName: userDetails.lastName?.trim(),
      phoneNumber: userDetails.phoneNumber?.trim(),
    };

    const userId = getUserId();
    const analyticsPayload = {
      eventName: "update_profile_click",
      eventCategory: getEventCategory(),
      eventLabel: "Screen name: Update your profile",
    };

    if (userId) analyticsPayload.user_uid = userId;

    trackButtonClick(analyticsPayload);

    try {
      setIsUserDetailsUpdating(true);
      const response = await updateUserDetails(payload);

      if (response?.status === "SUCCESS") {
        const response = await checkLoginStatus();

        dispatch(
          userData({
            userFirstName: response.userFirstName,
            userType: response.userType,
            isUserLoggedIn: true,
            user: response,
          })
        );

        getUserProfile().then((response) => {
          const data = response?.data;
          dispatch(setProfileInfo(data));
        });

        const isPhoneVerifiedResponse = await isPhoneNoVerified(
          userDetails.phoneNumber
        );
        if (
          isPhoneVerifiedResponse.data.status === "SUCCESS" &&
          !isPhoneVerifiedResponse?.data?.data
        ) {
          sendOTP();
        }
        setIsUpdateProfileModalActive(false);

        const userId = getUserId();
        const analyticsPayload = {
          eventName: "update_profile_success",
          eventCategory: getEventCategory(),
          eventLabel: "Screen name: Update your profile",
        };
        if (userId) analyticsPayload.user_uid = userId;

        trackApiCall(analyticsPayload);
      }
    } catch (error) {
      console.error("Error updating user details:", error);
    } finally {
      setIsUserDetailsUpdating(false);
    }
  };

  const handleChange = (e, index) => {
    const { value } = e.target;
    if (/^\d?$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);

      // Move to the next input field if a digit is entered
      if (value && index < otpInputRefs.current.length - 1) {
        otpInputRefs.current[index + 1].focus();
      }
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace") {
      if (otp[index] === "" && index > 0) {
        otpInputRefs.current[index - 1].focus();
      }
    }
  };

  const handleVerifyOTP = async () => {
    const otpString = otp.join("");
    const userId = getUserId();

    const analyticsPayload = {
      eventName: "verify_code_click",
      eventCategory: getEventCategory(),
      eventLabel: "Screen name: Phone verification",
    };
    if (userId) analyticsPayload.user_uid = userId;

    trackButtonClick(analyticsPayload);

    // Validation: Prevent submission if OTP is empty
    if (!otpString.trim()) {
      setErrorMessage("OTP is required.");
      return;
    }

    try {
      setIsOtpVerificationGoing(true);
      setErrorMessage("");

      const phoneNo = isFromGlobalOtpModal
        ? userDetails.phoneNumber
        : phoneNumber;
      const response = await validateVerificationCode({
        phoneNumber: phoneNo,
        verificationCode: otpString,
      });

      if (response?.status === "SUCCESS") {
        if (userData?.user?.idologyVerified === true) {
          trackEvent({
            name: "ready_to_bid",
            category: "New-Bid Wizard",
            eventSource: "on phone number Verification",
            eventLabel: "",
          });
        }
        setErrorMessage("");
        toast.success("Phone number verified successfully.");

        const userId = getUserId();
        const analyticsPayload = {
          eventName: "phone_verification_successful",
          eventCategory: getEventCategory(),
          eventLabel: "Screen name: Phone verification",
        };
        if (userId) analyticsPayload.user_uid = userId;

        trackApiCall(analyticsPayload);

        // Handle success (navigation, etc.)
        if (isFromGlobalOtpModal) {
          setIsUpdateProfileModalActive(false);
          // Navigate based on redirectScreen before closing modal
          redirectionRequried && handleRedirect(redirectScreen);

          onClose && onClose();
        } else {
          navigate("/");
        }
      } else if (response?.status === "denied") {
        setErrorMessage("Please enter valid verification code.");
      } else {
        setErrorMessage(
          (response?.message || "").startsWith("TECHNICAL_ERROR") ||
            (response?.errorCode || "").startsWith("TECHNICAL_ERROR")
            ? "We are facing technical difficulties, please try again later."
            : response?.message || ""
        );
      }
    } catch (error) {
      console.error("OTP verification failed:", error);
      setErrorMessage(
        "We are facing technical difficulties, please try again later."
      );
    } finally {
      setIsOtpVerificationGoing(false);
    }
  };

  // helper function to handle different redirect screens
  const handleRedirect = (screen) => {
    switch (screen) {
      case "HOME":
        navigate("/");
        break;
      case "PDP":
        navigate("/bid");
        break;
      default:
        navigate("/");
    }
  };

  const handleResendOTP = useCallback(async () => {
    try {
      setLoading(true);
      setErrorMessage(""); // Reset error message before API call

      const resendData = { phoneNumber: localStorage.getItem("phoneNumber") };
      const response = await resendVerificationCode(resendData);

      if (response?.status === "SUCCESS") {
        setOtp(["", "", "", ""]); // Reset OTP input fields
      } else {
        const apiErrorMessage = response?.errorCode || "";
        setErrorMessage(
          apiErrorMessage === "Invalid Request Parameters. Please check again."
            ? "We are facing technical difficulties, please try again later."
            : "Please try again later."
        );
      }
    } catch (error) {
      console.error("Resend failed:", error);
      // setErrorMessage("Failed to resend OTP");
      setErrorMessage(
        "We are facing technical difficulties, please try again later."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  const handleDoItLater = () => {
    console.log("Redirecting to Home");
    navigate("/");
  };

  const handleGenerateNewCode = (e) => {
    e.preventDefault();
    setResendOtp(true);
    sendOTP({ mannualClick: true });
    setShowProfileConfirmation(true);

    const userId = getUserId();
    const analyticsPayload = {
      eventName: "resend_sms_click",
      eventCategory: getEventCategory(),
      eventLabel: "Screen name: Phone verification",
    };
    if (userId) analyticsPayload.user_uid = userId;

    trackButtonClick(analyticsPayload);
  };

  const handleUpdateProfile = (e) => {
    e.preventDefault();
    isFromGlobalOtpModal
      ? setIsUpdateProfileModalActive(true)
      : setIsConfirmProfileActive(true);

    const userId = getUserId();
    const analyticsPayload = {
      eventName: "view_profile_click",
      eventCategory: getEventCategory(),
      eventLabel: "Screen name: Phone verification",
    };
    if (userId) analyticsPayload.user_uid = userId;

    trackButtonClick(analyticsPayload);
  };

  useEffect(() => {
    // Prevent multiple OTP sends with a flag
    let isSubscribed = true;

    const handleOTPSend = async () => {
      if (phoneNumber && !isFromGlobalOtpModal && isFromLoginSignupPage) {
        await sendOTP();
        return;
      }

      if (shouldSendOTP && userDetails.phoneNumber) {
        try {
          if (isSubscribed) {
            await sendOTP();
            setShouldSendOTP(false);

            // Focus on first OTP input after sending OTP
            if (otpInputRefs.current?.[0]) {
              otpInputRefs.current[0].focus();
            }
          }
        } catch (error) {
          console.error("Error sending OTP:", error);
        }
      }
    };

    handleOTPSend();

    // Cleanup function
    return () => {
      isSubscribed = false;
    };
  }, [
    phoneNumber,
    isFromGlobalOtpModal,
    shouldSendOTP,
    userDetails.phoneNumber,
  ]);

  useEffect(() => {
    if (userProfData) {
      setUserDetails({
        firstName: userProfData.firstName,
        lastName: userProfData.lastName,
        phoneNumber: userProfData.phoneNumber,
        email: userProfData.emailId,
      });
    }
  }, [userProfData]);

  useEffect(() => {
    const userId = getUserId();

    // Track component opening when mounted
    const trackComponentOpen = () => {
      const analyticsPayload = {
        eventName: "phone_verification_popup_open_click",
        eventCategory: getEventCategory(),
        eventSource: "OTP Verification Open",
      };

      // Add userId to payload if available
      if (userId) {
        analyticsPayload.user_uid = userId;
      }

      trackPageMount(analyticsPayload);
    };

    trackComponentOpen();

    // Track component closing when unmounted
    return () => {
      const closePayload = {
        eventName: "phone_verification_popup_close_click",
        eventCategory: getEventCategory(),
        eventSource: "OTP Verification Close",
      };

      // Add userId to close payload if available
      if (userId) {
        closePayload.user_uid = userId;
      }

      trackPageMount(closePayload);
    };
  }, [userProfData?.realbiduserid, userDataDetails?.realbiduserid]);

  return (
    <>
      {isUpdateProfileModalActive ? (
        <UserProfileForm
          userDetails={userDetails}
          errors={formError}
          changePhoneErrorMsg={changePhoneErrorMsg}
          onChange={handleUserDetailsChange}
          onSubmit={handleUpdateUserDetails}
          isSubmitting={isUserDetailsUpdating}
        />
      ) : (
        <div className="otp-verification-container">
          <h2 className="registration-confirmProfile-headers pt-0">
            Phone Number Verification
          </h2>
          <p className="phn-verify-subheader">
            Enter the Verification code sent to the Phone number ending in{" "}
            <p className="phn-text">
              {maskPhoneNumber(
                isFromGlobalOtpModal ? userDetails.phoneNumber : phoneNumber
              )}
            </p>
          </p>
          <p>
            {" "}
            {(errorMessage || otpErrorMessage) && (
              <div className="error-message">
                {errorMessage || otpErrorMessage}
              </div>
            )}{" "}
            {/* Display error message */}
          </p>

          <div className="otp-form">
            <h4 className="otp-header">Verify Your Phone Number</h4>
            <div className="otp-input-row d-flex align-items-center">
              <div className="otp-container">
                {otp.map((digit, index) => (
                  // <input
                  //   key={index}
                  //   type="text"
                  //   value={digit}
                  //   onChange={(e) => handleChange(e, index)}
                  //   maxLength="1"
                  //   className="otp-input"
                  //   ref={(el) => (otpInputRefs.current[index] = el)}
                  // />

                  <input
                    id={`otp-input-${index}`}
                    key={index}
                    type="text"
                    value={digit}
                    onChange={(e) => handleChange(e, index)}
                    onKeyDown={(e) => handleKeyDown(e, index)}
                    maxLength="1"
                    className="otp-input"
                    ref={(el) => (otpInputRefs.current[index] = el)}
                    disabled={errorMessage.includes("technical difficulties")} // Disable if error message is technical-related
                  />
                ))}
              </div>

              <Button
                id="otp-verify-btn"
                className="verify-now-btn ms-3 fs-12 hubzu-btn-thm"
                onClick={handleVerifyOTP}
                disabled={otp.join("").length !== 4}
              >
                {isOtpVerificationGoing ? (
                  <LoadingText text="Verifying..." />
                ) : (
                  <LoadedText text="Verify" />
                )}
              </Button>
              {!isModal && (
                <>
                  <span className="mx-2">Or</span>
                  <Button
                    id="otp-do-later-btn"
                    className="do-it-later-btn fs-12  hubzu-btn-thm"
                    onClick={handleDoItLater}
                  >
                    Do It Later
                  </Button>
                </>
              )}
            </div>
          </div>

          {isResendLoading && (
            <div className="loading-animation-container mt-3">
              <div className="loading-dots">
                <div className="loader-dot"></div>
                <div className="loader-dot"></div>
                <div className="loader-dot"></div>
              </div>
            </div>
          )}

          <p className="help-text">
            Didn't get the verification code?{" "}
            <a
              id="otp-resend-link"
              // href="/register-sign-in"
              className="verification-link next-navlink"
              onClick={handleGenerateNewCode}
            >
              Click here
            </a>{" "}
            to generate a new one or check your{" "}
            <a
              id="otp-profile-link"
              href="#"
              className="verification-link next-navlink"
              onClick={handleUpdateProfile}
            >
              profile
            </a>{" "}
            to confirm your phone number is correct.
          </p>
        </div>
      )}
    </>
  );
};

export default OTPVerification;
